/**
 * useConversationMembers — the active members of a conversation (conversation-members spec), for the composer's "To"
 * and the roster: each one by its address (membership id), the person looking at it (`me`) and the lead.
 *
 * An AI member that asks another member adds it to the conversation from the engine, which sends no member event; the
 * message it posts names the new member. So whenever the messages name an address the list does not hold, the list is
 * fetched again.
 */
import type { ConversationMemberRef } from '@confluent/shared/lib/conversation-addressing';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  listConversationMembersOptions,
  listConversationMembersQueryKey,
} from '../generated/api/@tanstack/react-query.gen.js';
import { isMe, toMemberRef, unknownMemberIds } from '../components/conversations/conversation-members-model.js';
import { useAuthStore } from '../store/index.js';
import type { StreamMessage } from './useConversationStream.js';

export interface ConversationMembers {
  members: ConversationMemberRef[];
  /** The person looking at the conversation, when they are a member. */
  me: ConversationMemberRef | null;
  lead: ConversationMemberRef | null;
  /** Each AI member's own thread under the conversation, by address (a member that has not answered has none). */
  threads: ReadonlyMap<number, number>;
  isLoading: boolean;
  isError: boolean;
}

export function useConversationMembers(
  conversationId: string | null,
  messages: readonly StreamMessage[],
  enabled = true,
): ConversationMembers {
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const query = useQuery({
    ...listConversationMembersOptions({ path: { id: conversationId ?? '' } }),
    enabled: enabled && conversationId !== null,
  });
  const active = (query.data ?? []).filter((member) => member.leftAt === null);
  const members = active.map(toMemberRef);
  const threads = new Map(
    active.flatMap((member) =>
      member.threadConversationId ? [[member.id, member.threadConversationId] as const] : [],
    ),
  );

  const unknown = query.isSuccess ? unknownMemberIds(messages, members).join(',') : '';
  useEffect(() => {
    if (!unknown || !conversationId) return;
    void queryClient.invalidateQueries({ queryKey: listConversationMembersQueryKey({ path: { id: conversationId } }) });
  }, [unknown, conversationId, queryClient]);

  return {
    members,
    me: members.find((member) => isMe(member, userId)) ?? null,
    lead: members.find((member) => member.role === 'lead') ?? null,
    threads,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
